// Music theory engine for "What's the Key?"
const LETTERS = ["C", "D", "E", "F", "G", "A", "B"];
const NATURAL_PC = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

export const TONICS = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

const INTERVALS = {
  Major: [0, 2, 4, 5, 7, 9, 11],
  Minor: [0, 2, 3, 5, 7, 8, 10],
  Ionian: [0, 2, 4, 5, 7, 9, 11],
  Dorian: [0, 2, 3, 5, 7, 9, 10],
  Phrygian: [0, 1, 3, 5, 7, 8, 10],
  Lydian: [0, 2, 4, 6, 7, 9, 11],
  Mixolydian: [0, 2, 4, 5, 7, 9, 10],
  Aeolian: [0, 2, 3, 5, 7, 8, 10],
  Locrian: [0, 1, 3, 5, 6, 8, 10],
};

function nameToLetterPc(name) {
  const letter = name[0];
  let pc = NATURAL_PC[letter];
  for (const ch of name.slice(1)) {
    if (ch === "#") pc = (pc + 1) % 12;
    else if (ch === "b") pc = (pc + 11) % 12;
  }
  return { letter, pc };
}

function accidental(diff) {
  if (diff > 6) diff -= 12;
  if (diff < -6) diff += 12;
  return { "0": "", "1": "#", "2": "##", "-1": "b", "-2": "bb" }[String(diff)] || "";
}

export function spell(tonic, modeName) {
  const intervals = INTERVALS[modeName] || INTERVALS.Major;
  const { letter, pc } = nameToLetterPc(tonic);
  const start = LETTERS.indexOf(letter);
  return intervals.map((semi, i) => {
    const targetPc = (pc + semi) % 12;
    const L = LETTERS[(start + i) % 7];
    const diff = (((targetPc - NATURAL_PC[L]) % 12) + 12) % 12;
    return L + accidental(diff);
  });
}

export function pcOf(name) {
  return nameToLetterPc(name).pc;
}

const TRIAD_QUALITY = {
  Major: ["", "m", "m", "", "", "m", "dim"],
  Minor: ["m", "dim", "", "m", "m", "", ""],
};
const SEVENTH_QUALITY = {
  Major: ["maj7", "m7", "m7", "maj7", "7", "m7", "m7b5"],
  Minor: ["m7", "m7b5", "maj7", "m7", "m7", "maj7", "7"],
};
const NUMERALS = {
  Major: ["I", "ii", "iii", "IV", "V", "vi", "vii°"],
  Minor: ["i", "ii°", "III", "iv", "v", "VI", "VII"],
};

export function diatonic(tonic, mode) {
  const scale = spell(tonic, mode);
  return scale.map((root, i) => {
    const q = TRIAD_QUALITY[mode][i];
    return {
      numeral: NUMERALS[mode][i],
      root,
      quality: q,
      name: root + (q === "dim" ? "dim" : q),
      seventh: SEVENTH_QUALITY[mode][i],
    };
  });
}

export function keySignature(tonic, mode) {
  const scale = spell(tonic, mode === "Minor" ? "Minor" : "Major");
  const sharps = scale.filter((n) => n.includes("#")).length;
  const flats = scale.filter((n) => n.includes("b")).length;
  if (!sharps && !flats) return "No sharps or flats";
  if (sharps) return `${sharps} sharp${sharps > 1 ? "s" : ""}`;
  return `${flats} flat${flats > 1 ? "s" : ""}`;
}

export function relative(tonic, mode) {
  const scale = spell(tonic, mode);
  return mode === "Major"
    ? { name: `${scale[5]} Minor`, tonic: scale[5], mode: "Minor" }
    : { name: `${scale[2]} Major`, tonic: scale[2], mode: "Major" };
}

const MODE_LIST = [
  ["Ionian", "Major", "Bright and resolved — the classic major sound."],
  ["Dorian", null, "Minor with a hopeful raised 6th. Jazzy and cool."],
  ["Phrygian", null, "Dark and tense with a flat 2nd — Spanish flavour."],
  ["Lydian", null, "Dreamy and floating thanks to the raised 4th."],
  ["Mixolydian", null, "Bluesy major with a flat 7th. Rock & funk staple."],
  ["Aeolian", "Natural Minor", "The natural minor scale — melancholic and moody."],
  ["Locrian", null, "Unstable and tense with a diminished tonic."],
];

export function modesOnTonic(tonic) {
  return MODE_LIST.map(([name, alias, desc]) => ({
    name,
    alias,
    desc,
    notes: spell(tonic, name),
  }));
}

const PROGS = [
  ["The Pop Axis", [0, 4, 5, 3]],
  ["Jazz Turnaround", [1, 4, 0]],
  ["Classic Cadence", [0, 3, 4]],
  ["50s Doo-Wop", [0, 5, 3, 4]],
];

export function progressions(tonic, mode) {
  const dia = diatonic(tonic, mode);
  return PROGS.map(([name, degs]) => ({ name, chords: degs.map((d) => dia[d]) }));
}

const CHORD_INTERVALS = {
  "": [0, 4, 7],
  m: [0, 3, 7],
  dim: [0, 3, 6],
  aug: [0, 4, 8],
  7: [0, 4, 7, 10],
  m7: [0, 3, 7, 10],
  maj7: [0, 4, 7, 11],
  m7b5: [0, 3, 6, 10],
  dim7: [0, 3, 6, 9],
  sus2: [0, 2, 7],
  sus4: [0, 5, 7],
};

export function chordMidis(rootName, quality, base = 48) {
  const rootPc = pcOf(rootName);
  const iv = CHORD_INTERVALS[quality] || CHORD_INTERVALS[""];
  return iv.map((i) => base + rootPc + i);
}

export function ascendingMidis(tonic, mode, octave = 4) {
  const pcs = spell(tonic, mode).map(pcOf);
  const out = [];
  let prev = -1;
  let oct = octave;
  for (const pc of pcs) {
    if (prev !== -1 && pc <= prev) oct++;
    out.push(12 * (oct + 1) + pc);
    prev = pc;
  }
  out.push(out[0] + 12);
  return out;
}

// ---- Guitar open-chord voicings (low E -> high E; null = muted, 0 = open) ----
const OPEN_SHAPES = {
  C: [null, 3, 2, 0, 1, 0],
  Cm: [null, 3, 5, 5, 4, 3],
  D: [null, null, 0, 2, 3, 2],
  Dm: [null, null, 0, 2, 3, 1],
  E: [0, 2, 2, 1, 0, 0],
  Em: [0, 2, 2, 0, 0, 0],
  F: [1, 3, 3, 2, 1, 1],
  Fm: [1, 3, 3, 1, 1, 1],
  G: [3, 2, 0, 0, 0, 3],
  Gm: [3, 5, 5, 3, 3, 3],
  A: [null, 0, 2, 2, 2, 0],
  Am: [null, 0, 2, 2, 1, 0],
  B: [null, 2, 4, 4, 4, 2],
  Bm: [null, 2, 4, 4, 3, 2],
};
const ENHARMONIC = { "Db": "C#", "Eb": "D#", "Gb": "F#", "Ab": "G#", "Bb": "A#" };

export function guitarShape(rootName, quality) {
  // normalize root to a letter we might have; try direct then enharmonic
  const suffix = quality === "m" ? "m" : "";
  let root = rootName;
  const key = root + suffix;
  if (OPEN_SHAPES[key]) return OPEN_SHAPES[key];
  // try flat->sharp
  const flat = root.replace(/[#b]/g, (m) => m);
  if (ENHARMONIC[root] && OPEN_SHAPES[ENHARMONIC[root] + suffix]) {
    return OPEN_SHAPES[ENHARMONIC[root] + suffix];
  }
  return null; // no open shape (use a barre)
}

const OPEN_MAJOR = { 0: "C", 7: "G", 2: "D", 9: "A", 4: "E" };
const OPEN_MINOR = { 9: "Am", 4: "Em", 2: "Dm" };

export function capoSuggestion(tonic, mode) {
  const tpc = pcOf(tonic);
  const openSet = mode === "Major" ? OPEN_MAJOR : OPEN_MINOR;
  if (openSet[tpc]) {
    return `${tonic} ${mode} already sits nicely under the fingers with open chords — no capo needed.`;
  }
  for (let capo = 1; capo <= 7; capo++) {
    const playedPc = ((tpc - capo) % 12 + 12) % 12;
    if (openSet[playedPc]) {
      return `Put a capo on fret ${capo} and play open ${openSet[playedPc]} shapes to sound in ${tonic} ${mode}.`;
    }
  }
  return `No easy open-chord capo position — use barre chords for ${tonic} ${mode}.`;
}

// ---- Enharmonic-friendly tonic spellings ----
const FLAT_NAMES = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];
const MINOR_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "Bb", "B"];

export function tonicsFor(mode) {
  return mode === "Minor" ? MINOR_NAMES : FLAT_NAMES;
}
export function preferredTonic(pc, mode) {
  return (mode === "Minor" ? MINOR_NAMES : FLAT_NAMES)[pc];
}

// Returns a playable guitar voicing descriptor for a chord.
export function guitarInfo(root, quality) {
  if (quality === "dim") return { type: "none" };
  const shape = guitarShape(root, quality);
  if (shape) return { type: "open", frets: shape };
  let fret = (((pcOf(root) - 4) % 12) + 12) % 12;
  if (fret === 0) fret = 12;
  return { type: "barre", fret, shape: quality === "m" ? "Em" : "E" };
}
